export const PLAYBACK_BAR_COUNT = 40;
export const RECORDING_BAR_COUNT = 28;

export function clampWaveLevel(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0.12;
  return Math.max(0.12, Math.min(1, n));
}

export function idleWaveBars(count = RECORDING_BAR_COUNT) {
  return Array.from({ length: count }, () => 0.08);
}

export function downsampleWaveform(samples, count = PLAYBACK_BAR_COUNT) {
  const length = Array.isArray(samples) ? samples.length : 0;
  if (!length) return Array.from({ length: count }, () => 0.12);
  if (length === count) return samples.map(clampWaveLevel);
  const out = [];
  const block = length / count;
  for (let i = 0; i < count; i += 1) {
    const start = Math.floor(i * block);
    const end = Math.max(start + 1, Math.floor((i + 1) * block));
    let max = 0;
    for (let j = start; j < end && j < length; j += 1) {
      const v = Number(samples[j]) || 0;
      if (v > max) max = v;
    }
    out.push(clampWaveLevel(max));
  }
  return out;
}

export function parseVoiceWaveform(value) {
  let list = value;
  if (typeof list === "string") {
    try {
      list = JSON.parse(list);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(list) || !list.length) return null;
  return downsampleWaveform(list.map((v) => Number(v)), PLAYBACK_BAR_COUNT);
}

export function peaksFromAudioBuffer(buffer, barCount = PLAYBACK_BAR_COUNT) {
  const data = buffer.getChannelData(0);
  const total = data.length;
  if (!total) return Array.from({ length: barCount }, () => 0.12);
  const peaks = [];
  for (let i = 0; i < barCount; i += 1) {
    const start = Math.floor((i * total) / barCount);
    const end = Math.max(start + 1, Math.floor(((i + 1) * total) / barCount));
    let max = 0;
    for (let j = start; j < end; j += 1) {
      const v = Math.abs(data[j] || 0);
      if (v > max) max = v;
    }
    peaks.push(max);
  }
  const hi = Math.max(...peaks, 0.01);
  return peaks.map((p) => clampWaveLevel(p / hi));
}

async function decodeAudioBuffer(blob) {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  try {
    await ctx.resume?.();
    const copy = (await blob.arrayBuffer()).slice(0);
    return await ctx.decodeAudioData(copy);
  } catch {
    return null;
  } finally {
    void ctx.close();
  }
}

export async function extractPeaksFromBlob(blob) {
  if (!blob || typeof window === "undefined") return null;
  const buffer = await decodeAudioBuffer(blob);
  if (!buffer) return null;
  return {
    waveform: peaksFromAudioBuffer(buffer, PLAYBACK_BAR_COUNT),
    durationMs: Math.round(buffer.duration * 1000),
  };
}

export async function extractPeaksFromUrl(url) {
  if (!url || typeof window === "undefined") return null;
  try {
    const res = url.startsWith("blob:")
      ? await fetch(url)
      : await fetch(url, { mode: "cors", credentials: "omit", cache: "force-cache" });
    if (!res.ok) return null;
    return extractPeaksFromBlob(await res.blob());
  } catch {
    return null;
  }
}
